/**
 * Page views and per-page dwell.
 *
 * @module logging/instrumentation/page-views
 */

import { sanitizePath } from '../core/sanitize';
import { boundedDwellMs, visibleMsThisLoad } from '../core/visible-clock';
import { telemetry } from '../telemetry';

/**
 * The previous path in this tab, held in memory only.
 *
 * Not persisted to `sessionStorage` alongside the session id, because a
 * reload should not report the pre-reload page as the referring route — the
 * navigation the field describes did not happen. Absent is the honest value
 * for the first view after a load.
 */
let previousPath: string | null = null;

/** Reading of the visible clock when the current page was entered. */
let enteredAtVisibleMs: number | null = null;

/**
 * Visible time on the page currently being viewed, or null before the
 * first view of this page load.
 *
 * Exists for `web_session_ended`, which is the only way the **exit page**
 * ever gets a dwell figure: it has no following `web_page_viewed` to carry
 * one, and on a marketing site the exit page is usually the interesting
 * one. Deriving dwell in SQL from consecutive page-view timestamps would
 * silently drop it.
 */
export function currentPageDwellMs(): number | null {
  if (enteredAtVisibleMs === null) return null;
  return boundedDwellMs(visibleMsThisLoad() - enteredAtVisibleMs);
}

/**
 * Record a route change, and with it the dwell of the page being left.
 *
 * The event's own `path` is stamped by the dispatcher from
 * `window.location`, which the History API has already updated by the time
 * a router effect runs. `previous_path` and `previous_dwell_ms` are the two
 * things the dispatcher cannot know, and they always describe the same
 * page — so either both are present or neither is.
 *
 * Dwell is measured on the visible clock, so a tab left in the background
 * accrues nothing; see `core/visible-clock.ts` for why wall-clock is the
 * wrong unit here.
 */
export function trackPageView(path: string): void {
  const current = sanitizePath(path);

  // A router can re-run its effect for the same path (a hash change, a
  // state-only navigation). Those are not new views, and counting them
  // would also reset the dwell clock on a page nobody left.
  if (current === previousPath) return;

  const leaving = previousPath;
  const leavingDwellMs = currentPageDwellMs();

  telemetry.emit(
    'web_page_viewed',
    leaving === null
      ? {}
      : {
          previous_path: leaving,
          ...(leavingDwellMs === null ? {} : { previous_dwell_ms: leavingDwellMs }),
        },
  );

  previousPath = current;
  enteredAtVisibleMs = visibleMsThisLoad();
}
